import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {
  BulkActionEvent,
  GateweeTableColumn,
  TableAction,
} from '@gatewee/interfaces';
import {PermissionPipe} from '@gatewee/helpers/pipes/permission.pipe';
import {getStatusColor} from '@gatewee/helpers';
import {
  isStructuredCellValue,
  stringifyStructuredCellValue,
} from '@gatewee/utils';
import {TuiDialogService} from '@taiga-ui/core';
import {PolymorpheusComponent} from '@taiga-ui/polymorpheus';
import {GateweeJsonPreviewDialogComponent} from '../gatewee-json-preview-dialog/gatewee-json-preview-dialog.component';

@Component({
  selector: 'gatewee-table-v2',
  templateUrl: './gatewee-table-v2.component.html',
  styleUrls: ['./gatewee-table-v2.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class GateweeTableV2Component<T> implements OnChanges, OnInit {
  @Input() data: T[] = [];
  @Input() columns!: GateweeTableColumn[];
  @Input() page = 1;
  @Input() pageSize = 10;
  @Input() hasCheckbox = false;
  @Input() selectAllAcrossFilter = false;
  @Input() activeFilters: Record<string, unknown> = {};
  /** Total row count. Pass `null`/`undefined` for uncounted (prev/next) pagination. */
  @Input() total: number | null | undefined = 1;
  /** Explicit has-more flag for uncounted pagination; otherwise inferred from page size. */
  @Input() hasMore?: boolean;
  @Input() showSettings = false;
  @Input() tableId = 'default';
  @Input() size: 's' | 'm' | 'l' = 'm';

  @Input() rowDetailTpl: TemplateRef<{$implicit: T}> | null = null;
  @Input() expandableWhen?: (row: T) => boolean;
  @Input() showExpandColumn = true;

  @Input() rowIdFn: (row: T, index: number) => string | number = (_r, i) => i;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() getActionDisabled: (row: any, key: string) => boolean = () => false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() getRowClass: (row: any) => string | string[] = () => '';

  @Output() newPage = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() outClick = new EventEmitter<{row: T; type: string}>();

  readonly pageSizeItems: readonly number[] = [10, 25, 50, 100];

  @Output() selectionChange = new EventEmitter<T[]>();
  @Output() selectAllAcrossFilterChange = new EventEmitter<boolean>();
  @Output() bulkAction = new EventEmitter<BulkActionEvent<T>>();

  @ViewChild('columnSettingsTpl') columnSettingsTpl!: TemplateRef<unknown>;

  constructor(
    private translate: TranslateService,
    private permissionPipe: PermissionPipe,
    private readonly dialogs: TuiDialogService,
  ) {}

  totalRows = 0;
  totalPages = 0;
  selectAll = false;
  settingsMenuOpen = false;
  pageSizeMenuOpen = false;

  private expanded = new Set<string | number>();
  private selectedRowIds = new Set<string | number>();

  trackByField = (_: number, col: GateweeTableColumn) => col.field;
  trackByIndex = (index: number) => index;
  trackByActionKey = (_: number, action: TableAction) => action.key;

  get isCountKnown(): boolean {
    return (
      this.total !== undefined &&
      this.total !== null &&
      Number.isFinite(this.total)
    );
  }

  resolveHasMore(): boolean {
    if (this.isCountKnown) {
      return this.page * this.pageSize < (this.total as number);
    }
    if (typeof this.hasMore === 'boolean') {
      return this.hasMore;
    }
    return (this.data?.length ?? 0) >= this.pageSize;
  }

  get canGoPrev(): boolean {
    return this.page > 1;
  }

  get canGoNext(): boolean {
    return this.resolveHasMore();
  }

  get showPaginationControls(): boolean {
    if (this.isCountKnown) {
      return this.totalPages > 1;
    }
    return this.canGoPrev || this.canGoNext;
  }

  get showPaginationFooter(): boolean {
    return !!(this.data?.length || (!this.isCountKnown && this.page > 1));
  }

  private calculatePagination(): void {
    if (this.isCountKnown) {
      this.totalRows = this.total as number;
      this.totalPages = Math.ceil(this.totalRows / this.pageSize);
      return;
    }
    this.totalRows = 0;
    this.totalPages = 0;
  }

  private getStorageKey(): string {
    return this.tableId;
  }

  private isMobileDevice(): boolean {
    const isMobileWidth = window.innerWidth < 768;
    const isMobileUserAgent =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );
    return isMobileWidth || isMobileUserAgent;
  }

  ngOnInit(): void {
    const saved = localStorage.getItem(this.getStorageKey());
    const isMobile = this.isMobileDevice();

    if (!this.tableId) {
      this.columns = this.columns.map(col => ({
        ...col,
        visible: true,
      }));
      return;
    }

    if (saved) {
      try {
        const prefs: {field: string; visible: boolean}[] = JSON.parse(saved);
        this.columns = this.columns.map(col => {
          const pref = prefs.find(p => p.field === col.field);
          const visible =
            col.alwaysVisible === true
              ? true
              : pref?.visible ?? col.visible ?? false;
          return {...col, visible};
        });
      } catch (e) {
        console.error('Erro ao restaurar preferências de colunas', e);
      }
    } else {
      this.columns = this.columns.map(col => {
        let visible: boolean;
        if (isMobile) {
          visible = col.alwaysVisible === true || col.field === 'actions';
        } else {
          visible = col.alwaysVisible ?? col.visible ?? false;
        }
        return {...col, visible};
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['data'] ||
      changes['total'] ||
      changes['pageSize'] ||
      changes['hasMore'] ||
      changes['page']
    ) {
      this.calculatePagination();
    }
    if (changes['data']) {
      this.selectedRowIds.clear();
      this.selectAll = false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onColumnToggle(_col: GateweeTableColumn): void {
    const prefs = this.columns.map(c => ({field: c.field, visible: c.visible}));
    localStorage.setItem(this.getStorageKey(), JSON.stringify(prefs));
  }

  getValueFromPath(obj: T, path: string, raw = false) {
    const def = this.columns.find(e => e.field === path);
    const value = path
      .split('.')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .reduce((acc: unknown, part: string) => (acc as any)?.[part], obj);
    if (def?.pipe && !raw) {
      return def.pipe.fn.transform(value, ...def.pipe.args);
    }
    return value;
  }

  isStructuredCellValue = isStructuredCellValue;

  openStructuredValuePreview(label: string, value: unknown): void {
    const translatedLabel = this.translate.instant(label);
    const title =
      translatedLabel !== label
        ? translatedLabel
        : this.translate.instant('common.actions.seeMore');

    this.dialogs
      .open<void>(
        new PolymorpheusComponent(GateweeJsonPreviewDialogComponent),
        {
          data: {
            title,
            content: stringifyStructuredCellValue(value),
          },
          size: 'm',
          dismissible: true,
          appearance: 'no-padding',
        },
      )
      .subscribe();
  }

  getStatusColor(status: string | boolean | number | null | undefined): string {
    return getStatusColor(status);
  }

  getBadgeStatusColor(col: GateweeTableColumn, row: T): string {
    const raw = this.getValueFromPath(row, col.field, true);
    const status = raw === null || raw === undefined ? '' : String(raw);
    if (col.statusColorFn) {
      return col.statusColorFn(status);
    }
    return this.getStatusColor(raw);
  }

  getStatusTextColor(
    status: string | boolean | number | null | undefined,
  ): string {
    const bgColor = this.getStatusColor(status);

    // Convert hex to RGB
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return white for dark backgrounds, dark for light backgrounds
    return luminance > 0.5 ? '#1A1A1A' : '#FFFFFF';
  }

  isRowSelected(row: T, index: number): boolean {
    if (this.selectAllAcrossFilter) return true;
    return this.selectedRowIds.has(this.rowIdFn(row, index));
  }

  /** Header checkbox reflects current-page selection only (not filter-wide). */
  isHeaderCheckboxChecked(): boolean {
    return this.isAllPageSelected();
  }

  isIndeterminate(): boolean {
    if (this.selectAllAcrossFilter) return false;
    return (
      this.selectedRowIds.size > 0 &&
      this.selectedRowIds.size < this.data.length
    );
  }

  isAllPageSelected(): boolean {
    return (
      this.data.length > 0 && this.selectedRowIds.size === this.data.length
    );
  }

  bannerState(): 'page' | 'all' | 'none' {
    if (!this.hasCheckbox || this.totalRows === 0) return 'none';
    if (this.selectAllAcrossFilter) return 'all';
    if (
      this.isAllPageSelected() &&
      this.totalRows > this.pageSize &&
      this.data.length > 0
    ) {
      return 'page';
    }
    return 'none';
  }

  onSelectAllAcrossFilterClick(): void {
    this.selectAllAcrossFilterChange.emit(true);
  }

  clearSelection(): void {
    this.selectedRowIds.clear();
    this.selectAll = false;
    this.selectAllAcrossFilterChange.emit(false);
    this.emitSelection();
  }

  toggleRowSelection(row: T, index: number, selected: boolean): void {
    if (this.selectAllAcrossFilter && !selected) {
      this.selectAllAcrossFilterChange.emit(false);
      this.data.forEach((r, i) => {
        if (i !== index) {
          this.selectedRowIds.add(this.rowIdFn(r, i));
        }
      });
      this.selectAll = this.isAllPageSelected();
      this.emitSelection();
      return;
    }

    const id = this.rowIdFn(row, index);
    selected ? this.selectedRowIds.add(id) : this.selectedRowIds.delete(id);
    this.selectAll = this.isAllPageSelected();
    this.emitSelection();
  }

  /** Table header checkbox: always toggles selection for the current page only. */
  checkUncheckAll(): void {
    if (this.selectAllAcrossFilter) {
      this.selectAllAcrossFilterChange.emit(false);
      this.selectedRowIds.clear();
      this.data.forEach((row, i) =>
        this.selectedRowIds.add(this.rowIdFn(row, i)),
      );
      this.selectAll = true;
      this.emitSelection();
      return;
    }

    const next = !this.isAllPageSelected();
    this.selectAll = next;
    if (next) {
      this.data.forEach((row, i) =>
        this.selectedRowIds.add(this.rowIdFn(row, i)),
      );
    } else {
      this.selectedRowIds.clear();
    }
    this.emitSelection();
  }

  private emitSelection(): void {
    const selected = this.getSelectedRows();
    this.selectionChange.emit(selected);
  }

  private getSelectedRows(): T[] {
    return this.data.filter((row, i) =>
      this.selectedRowIds.has(this.rowIdFn(row, i)),
    );
  }

  onBulkActionClick(action: TableAction): void {
    if (this.selectAllAcrossFilter) {
      this.bulkAction.emit({
        key: action.key,
        rows: [],
        allAcrossFilter: true,
        filters: this.activeFilters,
      });
      return;
    }

    const rows = this.getSelectedRows();
    if (rows.length === 0) return;
    this.bulkAction.emit({
      key: action.key,
      rows,
      allAcrossFilter: false,
      filters: this.activeFilters,
    });
  }

  getBulkActions(): TableAction[] {
    return this.getActionsColumn()?.bulkActions ?? [];
  }

  hasBulkActions(): boolean {
    return this.getBulkActions().length > 0;
  }

  hasSelection(): boolean {
    return this.selectAllAcrossFilter || this.selectedRowIds.size > 0;
  }

  showSelectionToolbar(): boolean {
    return this.hasCheckbox && this.hasSelection();
  }

  getSelectionCount(): number {
    if (this.selectAllAcrossFilter) {
      return this.totalRows;
    }
    return this.selectedRowIds.size;
  }

  shouldShowBulkAction(action: TableAction): boolean {
    const perm = action.condition?.['permission'];
    if (perm === undefined) return true;
    return this.permissionPipe.transform(perm as string);
  }

  isBulkActionDisabled(action: TableAction): boolean {
    if (this.selectAllAcrossFilter) {
      return typeof action.disabled === 'boolean' ? action.disabled : false;
    }
    const rows = this.getSelectedRows();
    const {disabled} = action;
    if (typeof disabled === 'function') {
      return rows.some(row => disabled(row));
    }
    return disabled ?? false;
  }

  getSummary(): string {
    const recordCount = this.data?.length ?? 0;
    if (!this.isCountKnown) {
      if (recordCount === 0) {
        return this.translate.instant('common.paginationPage', {
          page: this.page,
        });
      }
      const from = this.pageSize * (this.page - 1) + 1;
      const to = from + recordCount - 1;
      return this.translate.instant('common.paginationSummaryUnknown', {
        from,
        to,
      });
    }
    const from = this.pageSize * (this.page - 1) + 1;
    const to = Math.min(this.pageSize * this.page, this.totalRows);
    return this.translate.instant('common.paginationSummary', {
      from,
      to,
      total: this.totalRows,
    });
  }

  onPageIndexChange(index: number): void {
    this.newPage.emit(index + 1);
  }

  onPrevPage(): void {
    if (!this.canGoPrev) {
      return;
    }
    this.newPage.emit(this.page - 1);
  }

  onNextPage(): void {
    if (!this.canGoNext) {
      return;
    }
    this.newPage.emit(this.page + 1);
  }

  onRowsPerPageChange(value: number | string): void {
    const n = Number(value);
    if (!Number.isFinite(n) || n === this.pageSize) return;
    this.pageSizeChange.emit(n);
  }

  onPageSizeFromMenu(n: number): void {
    this.pageSizeMenuOpen = false;
    this.onRowsPerPageChange(n);
  }

  onClick(row: T, type = 'delete'): void {
    this.outClick.emit({row, type});
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onActionClick(row: any, action: TableAction) {
    this.onClick(row, action.key);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isActionDisabled(row: any, action: TableAction): boolean {
    if (typeof action.disabled === 'function') {
      return action.disabled(row);
    }
    return action.disabled ?? false;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  canExpandRow(row: T, _index: number): boolean {
    if (!this.rowDetailTpl || !this.showExpandColumn) return false;
    return this.expandableWhen ? !!this.expandableWhen(row) : true;
  }

  toggleExpand(row: T, index: number): void {
    if (!this.canExpandRow(row, index)) return;
    const id = this.rowIdFn(row, index);
    this.expanded.has(id) ? this.expanded.delete(id) : this.expanded.add(id);
  }

  isExpanded(row: T, index: number): boolean {
    return this.expanded.has(this.rowIdFn(row, index));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRowActions(invoice: any) {
    const base = this.columns.find(c => c.field === 'actions')?.actions ?? [];
    return base.map(action => ({
      ...action,
      disabled: this.getActionDisabled(invoice, action.key),
    }));
  }

  hasActions(): boolean {
    const actionsCol = this.columns.find(c => c.field === 'actions');
    if (!actionsCol) return false;
    const hasRow = !!actionsCol.actions && actionsCol.actions.length > 0;
    const hasBulk =
      !!actionsCol.bulkActions && actionsCol.bulkActions.length > 0;
    return hasRow || hasBulk;
  }

  getActionsColumn(): GateweeTableColumn | undefined {
    return this.columns.find(c => c.field === 'actions');
  }

  getActionIcon(actionKey: string): string {
    const iconMap: Record<string, string> = {
      view: '@phi.eye',
      viewDetails: '@phi.eye',
      details: '@phi.info',
      edit: '@phi.pencil-simple',
      delete: '@phi.trash-simple',
      duplicate: '@phi.copy-simple',
      archive: '@phi.archive-box',
      process: '@phi.arrows-clockwise',
      reprocess: '@phi.arrows-clockwise',
      retry: '@phi.arrows-clockwise',
      resend: '@phi.paper-plane-tilt',
      resendWebhook: '@phi.arrows-clockwise',
      webhook_update: '@phi.arrows-clockwise',
      download: '@phi.download-simple',
      downloadReceipt: '@phi.download-simple',
      copyKycLink: '@phi.copy-simple',
      healthCheck: '@phi.first-aid',
      feeConfiguration: '@phi.currency-dollar',
      settings: '@phi.gear',
      syncBalance: '@phi.arrows-clockwise',
      setPrimary: '@phi.star',
      resyncWallet: '@phi.wallet',
      refreshProvider: '@phi.arrows-clockwise',
      integrations: '@phi.plug',
      apiKeys: '@phi.key',
      users: '@phi.users-three',
      pixKeys: '@phi.plug',
      transactionsProvider: '@phi.arrows-left-right',
      bankAccounts: '@phi.bank',
    };
    return iconMap[actionKey] || '@tui.ellipsis';
  }

  resolveActionIcon(action: TableAction): string {
    const icon = action.icon;
    if (icon?.startsWith('@phi.') || icon?.startsWith('@tui.')) {
      return icon;
    }
    return this.getActionIcon(action.key);
  }

  shouldShowAction(row: T, action: TableAction): boolean {
    if (!action.condition) return true;
    return this.evaluateActionCondition(row, action);
  }

  evaluateActionCondition(row: T, action: TableAction): boolean {
    if (!action.condition) return true;

    return Object.keys(action.condition).every(key => {
      const value = action.condition?.[key];

      if (key === 'permission') {
        return this.permissionPipe.transform(value as string);
      }

      if (
        action?.condition?.[key] === true &&
        row[key as keyof T] !== undefined
      )
        return true;

      let isNot = false;
      let expectedValue = value;

      if (typeof value === 'string' && value.startsWith('!')) {
        isNot = true;
        expectedValue = value.slice(1);
      }

      const actualValue = this.getValueFromPath(row, key, true);

      if (expectedValue === '__exists__') {
        return actualValue !== undefined && actualValue !== null;
      }

      if (Array.isArray(expectedValue)) {
        return isNot
          ? !expectedValue.includes(actualValue)
          : expectedValue.includes(actualValue);
      }

      return isNot
        ? actualValue !== expectedValue
        : actualValue === expectedValue;
    });
  }
}
